import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  IconChevronLeft,
  IconClose,
  IconError,
  IconFailed,
  IconInfo,
  IconSuccess,
} from '../components/icons';
import { reviewAPI } from '../services/api';

// Inline alert banner (replaces native browser alert())
const InlineAlert = ({ alert, onDismiss }) => {
  if (!alert) return null;
  const styles = {
    error: { bg: 'bg-red-50 border-red-200 text-red-800', Icon: IconFailed, iconClass: 'text-red-500' },
    success: { bg: 'bg-green-50 border-green-200 text-green-800', Icon: IconSuccess, iconClass: 'text-green-500' },
    warning: { bg: 'bg-yellow-50 border-yellow-200 text-yellow-800', Icon: IconError, iconClass: 'text-yellow-500' },
    info: { bg: 'bg-blue-50 border-blue-200 text-blue-800', Icon: IconInfo, iconClass: 'text-blue-500' },
  };
  const { bg, Icon, iconClass } = styles[alert.type] || styles.info;
  return (
    <div className={`flex items-start gap-3 border rounded-lg px-4 py-3 text-sm ${bg}`}>
      <Icon className={`h-4 w-4 flex-shrink-0 mt-0.5 ${iconClass}`} />
      <span className="flex-1">{alert.message}</span>
      {onDismiss && (
        <button onClick={onDismiss} className="text-current opacity-60 hover:opacity-100"><IconClose className="h-3.5 w-3.5" /></button>
      )}
    </div>
  );
};

const ReviewDetail = () => {
  const { reviewId } = useParams();
  const navigate = useNavigate();
  const [review, setReview] = useState(null);
  const [actionHistory, setActionHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionInProgress, setActionInProgress] = useState(false);

  // Inline alerts (replaces all alert() calls)
  const [pageAlert, setPageAlert] = useState(null);
  const [dialogAlert, setDialogAlert] = useState(null);

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState({});
  const [editReason, setEditReason] = useState('');

  // Voice-audio regeneration state
  const [audioRegenerating, setAudioRegenerating] = useState(false);
  const [liveAudioUrl, setLiveAudioUrl] = useState(null); // tracks the latest audio URL
  const [audioStale, setAudioStale] = useState(false);    // true when script edited but not yet regen'd

  // Action dialog state
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [approverEmail, setApproverEmail] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  const fetchReview = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await reviewAPI.getDetail(reviewId);
      setReview(data.review);
      setActionHistory(data.action_history || []);
      setEditedContent({ ...data.review.generated_content });
      setLiveAudioUrl(data.review.generated_content?.audio_url || null);
      setAudioStale(false);
    } catch (err) {
      console.error('Failed to fetch review:', err);
      setError(err.userMessage || 'Failed to load review details.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReview();
  }, [reviewId]);

  // Close dialog helper – resets dialog alert and hides dialog
  const closeApproveDialog = () => {
    setShowApproveDialog(false);
    setDialogAlert(null);
  };
  const closeRejectDialog = () => {
    setShowRejectDialog(false);
    setDialogAlert(null);
  };

  // Handle resend — an approved review whose delivery failed.
  //
  // Approval and delivery are separate events and only approval could be
  // retried. A review approved while the provider was refusing ended as
  // "Approved, but NOT delivered ... fix the cause and resend" with nothing
  // to press: the actions panel offers nothing on an approved review. The
  // content was agreed, nobody was contacted, and there was no way forward.
  const handleResend = async () => {
    const who = approverEmail.trim() || review?.metadata?.reviewer_email;
    if (!who) {
      setPageAlert({ type: 'error', message: 'Please enter your email address first.' });
      return;
    }
    setActionInProgress(true);
    setPageAlert(null);
    try {
      const result = await reviewAPI.resend(reviewId, { resent_by: who });
      if (result?.delivery_error) {
        setPageAlert({
          type: 'error',
          message: `Resend failed: ${result.delivery_error} — no recipients were contacted.`,
        });
      } else {
        setPageAlert({
          type: 'success',
          message: `Resent successfully${
            result?.recipients_delivered ? ` to ${result.recipients_delivered} recipient(s)` : ''
          }.`,
        });
      }
      await fetchReview();
    } catch (e) {
      setPageAlert({
        type: 'error',
        message: e?.response?.data?.detail || 'Resend failed.',
      });
    } finally {
      setActionInProgress(false);
    }
  };

  // Handle approve
  const handleApprove = async (triggerDelivery = true) => {
    if (!approverEmail.trim()) {
      setDialogAlert({ type: 'error', message: 'Please enter your email address.' });
      return;
    }
    setActionInProgress(true);
    try {
      const result = await reviewAPI.approve(reviewId, {
        approved_by: approverEmail,
        notes: approvalNotes,
        trigger_delivery: triggerDelivery,
      });

      // The response is no longer discarded. Two outcomes arrive as HTTP 200
      // and used to be treated as plain success:
      //   success=false      the approval was declined (already approved /
      //                      rejected / the store was unavailable). Navigating
      //                      away here told the reviewer it had gone out.
      //   delivery_error     approved, but no recipient was reached. That is
      //                      the one thing a reviewer must never miss.
      if (result?.success === false) {
        setDialogAlert({
          type: 'error',
          message: result.message || 'This review could not be approved.',
        });
        return;
      }

      if (triggerDelivery && result?.delivery_error) {
        closeApproveDialog();
        setPageAlert({
          type: 'error',
          message: `Approved, but NOT delivered: ${result.delivery_error} — no recipients were contacted. Fix the cause and resend.`,
        });
        await fetchReview();
        return;
      }

      closeApproveDialog();
      navigate('/review-queue');
    } catch (err) {
      console.error('Approval failed:', err);
      setDialogAlert({
        type: 'error',
        message: err.userMessage || 'Failed to approve review. Please try again.',
      });
    } finally {
      setActionInProgress(false);
    }
  };

  // Handle reject
  const handleReject = async () => {
    if (!approverEmail.trim()) {
      setDialogAlert({ type: 'error', message: 'Please enter your email address.' });
      return;
    }
    if (!rejectionReason.trim()) {
      setDialogAlert({ type: 'error', message: 'Please provide a rejection reason.' });
      return;
    }
    setActionInProgress(true);
    try {
      await reviewAPI.reject(reviewId, {
        rejected_by: approverEmail,
        reason: rejectionReason,
      });
      closeRejectDialog();
      navigate('/review-queue');
    } catch (err) {
      console.error('Rejection failed:', err);
      setDialogAlert({
        type: 'error',
        message: err.userMessage || 'Failed to reject review. Please try again.',
      });
    } finally {
      setActionInProgress(false);
    }
  };

  // Handle save edits
  const handleSaveEdits = async (autoApprove = false) => {
    if (!approverEmail.trim()) {
      setPageAlert({ type: 'error', message: 'Please enter your email address.' });
      return;
    }
    if (!editReason.trim()) {
      setPageAlert({ type: 'error', message: 'Please provide a reason for the edits.' });
      return;
    }

    setActionInProgress(true);
    try {
      const edits = [];
      const originalContent = review.generated_content;
      Object.keys(editedContent).forEach((key) => {
        const origVal = JSON.stringify(originalContent[key] ?? null);
        const newVal = JSON.stringify(editedContent[key] ?? null);
        if (origVal !== newVal) {
          edits.push({
            field_name: key,
            original_value: origVal,
            edited_value: newVal,
            edit_reason: editReason,
            edited_by: approverEmail,
          });
        }
      });

      if (edits.length === 0 && !audioStale) {
        setPageAlert({ type: 'info', message: 'No changes detected — nothing to save.' });
        setActionInProgress(false);
        return;
      }

      const isVoice = review.content_type === 'voice_script';
      const needsRegen = isVoice && audioStale;

      // Step 1: Always save edits first with auto_approve=false.
      // This ensures generated_content["script"] is updated in the backend
      // BEFORE we ask ElevenLabs to synthesise audio from it.
      // We never pass auto_approve=true here — we control delivery timing below.
      if (edits.length > 0) {
        await reviewAPI.edit(reviewId, {
          edits,
          edited_by: approverEmail,
          auto_approve: false,
          edit_notes: editReason,
        });
      }

      // Step 2: Regenerate audio NOW — generated_content["script"] is already
      // updated on the server from Step 1, so ElevenLabs reads the new text.
      if (needsRegen) {
        setAudioRegenerating(true);
        try {
          const regenResult = await reviewAPI.regenerateAudio(reviewId);
          setLiveAudioUrl(regenResult.audio_url);
          setAudioStale(false);
        } catch (regenErr) {
          setPageAlert({
            type: autoApprove ? 'error' : 'warning',
            message: regenErr.userMessage || 'Edits saved, but audio regeneration failed. Please try again before sending.',
          });
          return;
        } finally {
          setAudioRegenerating(false);
        }
      }

      // Step 3: Trigger delivery only after audio (if any) is confirmed fresh.
      if (autoApprove) {
        const result = await reviewAPI.approve(reviewId, {
          approved_by: approverEmail,
          notes: editReason,
          trigger_delivery: true,
        });
        // Same contract as the approve dialog: a declined claim and a failed
        // delivery both come back as HTTP 200 and must not read as success.
        if (result?.success === false) {
          setPageAlert({
            type: 'error',
            message: result.message || 'This review could not be approved.',
          });
          await fetchReview();
          return;
        }
        if (result?.delivery_error) {
          setIsEditing(false);
          setPageAlert({
            type: 'error',
            message: `Approved, but NOT delivered: ${result.delivery_error} — no recipients were contacted.`,
          });
          await fetchReview();
          return;
        }
        navigate('/review-queue');
      } else {
        setIsEditing(false);
        setPageAlert({
          type: 'success',
          message: needsRegen ? 'Edits saved and audio regenerated.' : 'Edits saved successfully.',
        });
        fetchReview();
      }
    } catch (err) {
      console.error('Edit failed:', err);
      setPageAlert({
        type: 'error',
        message: err.userMessage || 'Failed to save edits. Please try again.',
      });
    } finally {
      setActionInProgress(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      in_review: 'bg-blue-100 text-blue-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      edited: 'bg-purple-100 text-purple-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Intl.DateTimeFormat('en-US', {
      month: 'long', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    }).format(new Date(dateString));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500" />
      </div>
    );
  }

  if (error || !review) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <IconError className="h-5 w-5 flex-shrink-0" />
          {error || 'Review not found'}
        </div>
        <button
          onClick={() => navigate('/review-queue')}
          className="mt-4 px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
        >
          Back to Review Queue
        </button>
      </div>
    );
  }

  // Approved, but the delivery that followed it did not reach anyone.
  // record_delivery_outcome writes this onto the review precisely so the two
  // states can be told apart: approved-and-sent looks identical to
  // approved-and-silently-failed without it.
  const deliveryOutcome = review?.metadata?.delivery;
  const deliveryFailed =
    String(review?.status).toLowerCase() === 'approved' &&
    !!deliveryOutcome &&
    deliveryOutcome.delivered === false;

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/review-queue')}
          className="mb-4 text-primary-600 hover:text-primary-800 flex items-center text-sm"
        >
          <IconChevronLeft className="h-4 w-4" /> Back to Review Queue
        </button>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">
              {review.campaign_name || review.disease_name}
            </h1>
            <p className="text-sm text-gray-500">Review ID: {review.review_id}</p>
          </div>
          <span className={`px-3 py-1.5 rounded-full text-xs font-medium ${getStatusColor(review.status)}`}>
            {review.status.replace('_', ' ').toUpperCase()}
          </span>
        </div>
      </div>

      {/* Page-level inline alert */}
      {pageAlert && (
        <div className="mb-4">
          <InlineAlert alert={pageAlert} onDismiss={() => setPageAlert(null)} />
        </div>
      )}

      {/* Unverified claims warning */}
      {review.unverified_claims_count > 0 && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-r-lg">
          <div className="flex items-start gap-3">
            <IconError className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-red-800">Medical Accuracy Warning</h3>
              <p className="text-sm text-red-700 mt-0.5">
                This content contains {review.unverified_claims_count} medical claim(s) that could not be
                verified against the provided documentation. Review carefully before approval.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Campaign Info */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Campaign Information</h2>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Health Topic</p>
                <p className="font-medium">{review.disease_name || '—'}</p>
              </div>
              <div>
                <p className="text-gray-500">Content Type</p>
                <p className="font-medium">{review.content_type}</p>
              </div>
              <div>
                <p className="text-gray-500">Language</p>
                <p className="font-medium">{review.language}</p>
              </div>
              <div>
                <p className="text-gray-500">Recipients</p>
                <p className="font-medium">{review.recipients_count}</p>
              </div>
              {review.campaign_goals && (
                <div className="col-span-2">
                  <p className="text-gray-500">Campaign Goals</p>
                  <p className="font-medium">{review.campaign_goals}</p>
                </div>
              )}
            </div>
          </div>

          {/* Generated Content */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Generated Content</h2>
              {(review.status === 'pending' || review.status === 'edited') && !isEditing && (
                <button
                  onClick={() => { setIsEditing(true); setPageAlert(null); }}
                  className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
                >
                  Edit Content
                </button>
              )}
              {isEditing && (
                <button
                  onClick={() => { setIsEditing(false); setPageAlert(null); }}
                  className="px-3 py-1.5 bg-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-300"
                >
                  Cancel Edit
                </button>
              )}
            </div>

            {isEditing ? (
              <div className="space-y-4">
                {review.content_type === 'sms' && editedContent.messages && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">SMS Messages</label>
                    {editedContent.messages.map((msg, idx) => (
                      <div key={idx} className="mb-3">
                        <textarea
                          value={msg}
                          onChange={(e) => {
                            const newMessages = [...editedContent.messages];
                            newMessages[idx] = e.target.value;
                            setEditedContent({ ...editedContent, messages: newMessages });
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                          rows="3"
                          maxLength="160"
                        />
                        <p className="text-xs text-gray-500 mt-1">{msg.length}/160 characters</p>
                      </div>
                    ))}
                  </div>
                )}
                {review.content_type === 'voice_script' && editedContent.script !== undefined && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Voice Script</label>
                    <textarea
                      value={editedContent.script}
                      onChange={(e) => {
                        setEditedContent({ ...editedContent, script: e.target.value });
                        setAudioStale(true);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                      rows="8"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {editedContent.script.split(/\s+/).filter(Boolean).length} words
                    </p>
                    {audioStale && (
                      <p className="text-xs text-amber-600 mt-1 font-medium">
                        Audio is outdated — it will be regenerated automatically when you save.
                      </p>
                    )}
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reason for Edit *</label>
                  <textarea
                    value={editReason}
                    onChange={(e) => setEditReason(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                    rows="2"
                    placeholder="Explain why you're making these changes..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Your Email *</label>
                  <input
                    type="email"
                    value={approverEmail}
                    onChange={(e) => setApproverEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                    placeholder="your.email@carearena.org"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => handleSaveEdits(false)}
                    disabled={actionInProgress}
                    className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {actionInProgress ? 'Saving…' : 'Save Edits'}
                  </button>
                  <button
                    onClick={() => handleSaveEdits(true)}
                    disabled={actionInProgress}
                    className="px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {actionInProgress ? 'Saving…' : 'Save & Approve'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {review.content_type === 'sms' && review.generated_content.messages && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">
                      SMS Messages ({review.generated_content.messages.length})
                    </h3>
                    {review.generated_content.messages.map((msg, idx) => (
                      <div key={idx} className="bg-gray-50 p-3 rounded-md mb-2 border border-gray-100">
                        <p className="text-sm text-gray-900">{msg}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          Message {idx + 1} · {msg.length} characters
                        </p>
                      </div>
                    ))}
                  </div>
                )}
                {review.content_type === 'voice_script' && review.generated_content.script && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Voice Script</h3>
                    <div className="bg-gray-50 p-4 rounded-md border border-gray-100">
                      <p className="text-sm text-gray-900 whitespace-pre-wrap">
                        {review.generated_content.script}
                      </p>
                    </div>
                    {audioRegenerating ? (
                      <div className="mt-3 flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded-md">
                        <svg className="animate-spin h-4 w-4 flex-shrink-0" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                        </svg>
                        Regenerating audio from updated script…
                      </div>
                    ) : liveAudioUrl ? (
                      <audio key={liveAudioUrl} controls className="w-full mt-3">
                        <source src={liveAudioUrl} type="audio/mpeg" />
                      </audio>
                    ) : null}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Claim Verifications */}
          {review.claim_verifications && review.claim_verifications.length > 0 && (
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Medical Claim Verifications</h2>
              <div className="space-y-3">
                {review.claim_verifications.map((claim, idx) => (
                  <div
                    key={idx}
                    className={`p-4 rounded-md border ${
                      claim.verified ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {claim.verified ? (
                        <IconSuccess className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      ) : (
                        <IconError className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                      )}
                      <div>
                        <p className="text-sm font-medium text-gray-900">{claim.claim}</p>
                        {claim.notes && (
                          <p className="text-xs text-gray-600 mt-1">{claim.notes}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action History */}
          {actionHistory.length > 0 && (
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Action History</h2>
              <div className="space-y-3">
                {actionHistory.map((action, idx) => (
                  <div key={idx} className="flex items-start border-l-2 border-gray-200 pl-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {action.action_type.replace('_', ' ').toUpperCase()}
                      </p>
                      <p className="text-xs text-gray-500">by {action.performed_by}</p>
                      <p className="text-xs text-gray-400">{formatDate(action.performed_at)}</p>
                      {action.notes && (
                        <p className="text-xs text-gray-700 mt-1">{action.notes}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar – Actions */}
        <div className="lg:col-span-1">
          <div className="bg-white p-6 rounded-lg border border-gray-200 sticky top-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Actions</h2>
            {(review.status === 'pending' || review.status === 'edited') ? (
              <div className="space-y-3">
                {review.content_type === 'voice_script' && audioStale && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded-md">
                    Save your script edits first — audio will regenerate automatically before sending.
                  </p>
                )}
                <button
                  onClick={() => { setShowApproveDialog(true); setDialogAlert(null); }}
                  disabled={actionInProgress || audioRegenerating || (review.content_type === 'voice_script' && audioStale)}
                  className="w-full px-4 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 font-medium text-sm"
                >
                  {audioRegenerating ? 'Regenerating audio…' : 'Approve & Send'}
                </button>
                <button
                  onClick={() => { setShowRejectDialog(true); setDialogAlert(null); }}
                  disabled={actionInProgress}
                  className="w-full px-4 py-3 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 font-medium text-sm"
                >
                  Reject
                </button>
              </div>
            ) : deliveryFailed ? (
              <div className="space-y-3">
                <p className="text-sm text-gray-600">
                  This review was approved but nothing was delivered. Fix the
                  cause, then resend.
                </p>
                <input
                  type="email"
                  value={approverEmail}
                  onChange={(e) => setApproverEmail(e.target.value)}
                  placeholder="your.email@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
                <button
                  onClick={handleResend}
                  disabled={actionInProgress}
                  className="w-full px-4 py-3 bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50 font-medium text-sm"
                >
                  {actionInProgress ? 'Resending…' : 'Resend'}
                </button>
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">
                No actions available for <strong>{review.status}</strong> reviews.
              </p>
            )}

            {/* Metadata */}
            <div className="mt-6 pt-6 border-t border-gray-200 space-y-3 text-sm">
              <div>
                <p className="text-gray-500">Created</p>
                <p className="font-medium">{formatDate(review.created_at)}</p>
              </div>
              <div>
                <p className="text-gray-500">Updated</p>
                <p className="font-medium">{formatDate(review.updated_at)}</p>
              </div>
              {review.metadata?.priority && (
                <div>
                  <p className="text-gray-500">Priority</p>
                  <p className="font-medium capitalize">{review.metadata.priority}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Approve Dialog */}
      {showApproveDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Approve Content</h3>
            {dialogAlert && (
              <div className="mb-4">
                <InlineAlert alert={dialogAlert} onDismiss={() => setDialogAlert(null)} />
              </div>
            )}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Your Email *</label>
                <input
                  type="email"
                  value={approverEmail}
                  onChange={(e) => setApproverEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                  placeholder="your.email@carearena.org"
                  disabled={actionInProgress}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Approval Notes <span className="text-gray-400">(optional)</span>
                </label>
                <textarea
                  value={approvalNotes}
                  onChange={(e) => setApprovalNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                  rows="2"
                  placeholder="Add any notes about the approval..."
                  disabled={actionInProgress}
                />
              </div>
              <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-md">
                <p className="text-xs text-yellow-800">
                  This will send content to {review.recipients_count} recipient(s).
                </p>
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button
                onClick={() => handleApprove(true)}
                disabled={actionInProgress}
                className="flex-1 px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionInProgress ? 'Processing…' : 'Approve & Send'}
              </button>
              <button
                onClick={closeApproveDialog}
                disabled={actionInProgress}
                className="px-4 py-2 bg-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-300 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Dialog */}
      {showRejectDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Reject Content</h3>
            {dialogAlert && (
              <div className="mb-4">
                <InlineAlert alert={dialogAlert} onDismiss={() => setDialogAlert(null)} />
              </div>
            )}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Your Email *</label>
                <input
                  type="email"
                  value={approverEmail}
                  onChange={(e) => setApproverEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                  placeholder="your.email@carearena.org"
                  disabled={actionInProgress}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rejection Reason *</label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                  rows="3"
                  placeholder="Explain why this content is being rejected..."
                  disabled={actionInProgress}
                />
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button
                onClick={handleReject}
                disabled={actionInProgress}
                className="flex-1 px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionInProgress ? 'Processing…' : 'Reject'}
              </button>
              <button
                onClick={closeRejectDialog}
                disabled={actionInProgress}
                className="px-4 py-2 bg-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-300 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewDetail;
